// Each theme: slate = 11 shades (50→950), acc = 11 accent shades (50→950).
// Dark themes: 950 = deepest bg, 50 = brightest text. Light themes: reversed (950 = page, 50 = ink).
export const SHADES = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"];
const LIGHT_SWAP = { 0: 10, 1: 9, 2: 8, 3: 7, 4: 6 };

const FONTS = {
  plex: { body: "'IBM Plex Sans', -apple-system, sans-serif", head: "'Chivo', 'IBM Plex Sans', sans-serif", num: "'JetBrains Mono', ui-monospace, monospace" },
  inter: { body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", head: "'Inter', -apple-system, sans-serif", num: "'Inter', -apple-system, sans-serif" },
  apple: { body: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif", head: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif", num: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif" },
  code: { body: "'Inter', -apple-system, sans-serif", head: "'Inter', -apple-system, sans-serif", num: "'JetBrains Mono', ui-monospace, monospace" },
};

const t = (key, name, group, mode, font, radius, slate, acc, extra = {}) => ({ key, name, group, mode, font, radius, slate, acc, ...extra });

export const THEMES = [
  t("warm-paper", "Warm Paper", "Apple style", "light", "inter", "14px",
    ["#141312", "#1F1D1B", "#2B2926", "#4A4741", "#75716A", "#9A958D", "#DAD6CE", "#E8E5DF", "#FFFFFF", "#F4F3EF", "#F8F7F4"],
    ["#FBF1EC", "#F6DED3", "#EDBBA6", "#E0906F", "#CC6640", "#B5441F", "#9C3A1A", "#7F2F15", "#632511", "#4A1C0D", "#2E1108"],
    { tag: "Recommended" }),
  t("macbook-light", "MacBook Light", "Apple style", "light", "apple", "14px",
    ["#000000", "#1D1D1F", "#2C2C2E", "#3A3A3C", "#6E6E73", "#8E8E93", "#D2D2D7", "#E5E5EA", "#FFFFFF", "#F5F5F7", "#FBFBFD"],
    ["#EAF3FD", "#CFE3FA", "#9FC7F5", "#5FA3EE", "#2F86E8", "#0071E3", "#0062C4", "#00509F", "#003F7D", "#002E5B", "#001D3A"]),
  t("macbook-dark", "MacBook Dark", "Apple style", "dark", "apple", "14px",
    ["#FFFFFF", "#F5F5F7", "#E5E5EA", "#C7C7CC", "#98989D", "#8E8E93", "#48484A", "#3A3A3C", "#2C2C2E", "#1C1C1E", "#141414"],
    ["#E6F2FF", "#CCE4FF", "#99C9FF", "#66AEFF", "#3393FF", "#0A84FF", "#0070E0", "#005BB8", "#00468F", "#003266", "#001F40"]),
  t("dark-pro", "Dark Pro", "Apple style", "dark", "apple", "12px",
    ["#FFFFFF", "#F2F2F2", "#E0E0E0", "#BDBDBD", "#9A9A9A", "#6B6B6B", "#3D3D3D", "#2A2A2A", "#161616", "#0A0A0A", "#000000"],
    ["#FFF5E6", "#FFE7C2", "#FFD08A", "#FFBB52", "#FFAD2E", "#FF9F0A", "#E08600", "#B86E00", "#8F5500", "#663D00", "#402600"], { onAcc: "dark" }),
  t("frosted-glass", "Frosted Glass", "Apple style", "dark", "apple", "18px",
    ["#FFFFFF", "#F4F7F8", "#E3EBEE", "#C5D3D8", "#9DB0B7", "#74878E", "#4E5F66", "#3C4C52", "#24343A", "#14232A", "#0C181D"],
    ["#E9F9FE", "#CFF1FC", "#A0E3F9", "#79D6F7", "#64CEF8", "#5AC8FA", "#2FB1EC", "#1A8FC4", "#156F98", "#10506E", "#0A3245"],
    { glass: true, onAcc: "dark" }),
  t("nord", "Nord", "Coding classics", "dark", "code", "8px",
    ["#ECEFF4", "#E5E9F0", "#D8DEE9", "#C0C8D6", "#9AA5B8", "#8C99B0", "#4C566A", "#434C5E", "#3B4252", "#2E3440", "#272C36"],
    ["#F0F8FA", "#DFF0F4", "#C3E3EB", "#A6D5E1", "#97CBD9", "#88C0D0", "#6AA9BB", "#5E81AC", "#4C6A91", "#3B5373", "#2A3B52"], { onAcc: "dark" }),
  t("dracula", "Dracula", "Coding classics", "dark", "code", "8px",
    ["#F8F8F2", "#F1F1EB", "#E2E2DC", "#BFC1D4", "#9EA3C4", "#8A94C2", "#4D5370", "#44475A", "#343746", "#282A36", "#21222C"],
    ["#F7F1FF", "#EDE1FE", "#DCC6FD", "#CDAEFB", "#C5A1FA", "#BD93F9", "#A274F0", "#8656DB", "#6A41B0", "#4F3084", "#352058"], { onAcc: "dark" }),
  t("one-dark", "One Dark", "Coding classics", "dark", "code", "8px",
    ["#FFFFFF", "#E6E9EF", "#D7DAE0", "#ABB2BF", "#8B92A0", "#7F848E", "#4B5263", "#3E4451", "#2C313A", "#282C34", "#21252B"],
    ["#EEF6FD", "#DCEDFB", "#B9DBF7", "#96C9F4", "#7BBCF1", "#61AFEF", "#3E98E6", "#2179C4", "#1A5E99", "#13446F", "#0C2B47"], { onAcc: "dark" }),
  t("tokyo-night", "Tokyo Night", "Coding classics", "dark", "code", "8px",
    ["#E9ECFB", "#D5D9F0", "#C0CAF5", "#A9B1D6", "#8890B8", "#737AA2", "#3B4261", "#2F3549", "#1F2335", "#1A1B26", "#16161E"],
    ["#F0F4FE", "#E1E9FD", "#C4D4FC", "#A6BEFA", "#90B0F8", "#7AA2F7", "#5583F2", "#3462D9", "#284CAB", "#1E387E", "#142552"], { onAcc: "dark" }),
  t("github-dark", "GitHub Dark", "Coding classics", "dark", "code", "8px",
    ["#FFFFFF", "#F0F6FC", "#E6EDF3", "#C9D1D9", "#8B949E", "#848D97", "#484F58", "#30363D", "#161B22", "#0D1117", "#010409"],
    ["#EBF3FE", "#D5E6FD", "#ABCDFB", "#7DB2F9", "#5499F8", "#2F81F7", "#1A6AE0", "#1554B3", "#103F86", "#0B2B5C", "#071B3A"]),
  t("github-light", "GitHub Light", "Coding classics", "light", "code", "8px",
    ["#010409", "#1F2328", "#24292F", "#424A53", "#59636E", "#818B98", "#D0D7DE", "#D8DEE4", "#FFFFFF", "#F6F8FA", "#FAFBFC"],
    ["#DDF4FF", "#B6E3FF", "#80CCFF", "#54AEFF", "#218BFF", "#0969DA", "#0550AE", "#033D8B", "#0A3069", "#002155", "#001237"]),
  t("solarized-dark", "Solarized Dark", "Coding classics", "dark", "code", "8px",
    ["#FDF6E3", "#EEE8D5", "#D9D3C0", "#93A1A1", "#839496", "#7A8F96", "#2A4A55", "#1C4250", "#073642", "#002B36", "#00212B"],
    ["#FCEEE8", "#F8D9CC", "#F0B399", "#E58A63", "#D96A3A", "#CB4B16", "#AD3F12", "#8C330E", "#6B270B", "#4D1C08", "#2E1105"]),
  t("solarized-light", "Solarized Light", "Coding classics", "light", "code", "8px",
    ["#002B36", "#073642", "#3A4F57", "#586E75", "#657B83", "#73868C", "#D9D3C0", "#E4DDC8", "#FDF6E3", "#EEE8D5", "#F5EFDC"],
    ["#EAF4FB", "#D2E7F6", "#A5CFED", "#73B4E3", "#4A9FDA", "#268BD2", "#1F75B3", "#195E90", "#13486E", "#0D334E", "#081F30"]),
  t("classic", "Classic Factory", "Original", "dark", "plex", "0.25rem", null, null),
];

export const DEFAULT_THEME = "warm-paper";
export const themeByKey = (k) => THEMES.find((x) => x.key === k) || THEMES.find((x) => x.key === DEFAULT_THEME);

const rgb = (hex) => { const h = hex.replace("#", ""); return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(" "); };

export function applyTheme(key) {
  const th = themeByKey(key);
  const root = document.documentElement;
  SHADES.forEach((s, i) => {
    ["slate", "orange"].forEach((p) => root.style.removeProperty(`--tw-${p}-${s}`));
    if (th.slate) root.style.setProperty(`--tw-slate-${s}`, rgb(th.slate[i]));
    if (th.acc) {
      const j = th.mode === "light" && LIGHT_SWAP[i] !== undefined ? LIGHT_SWAP[i] : i;
      root.style.setProperty(`--tw-orange-${s}`, rgb(th.acc[j]));
    }
  });
  const f = FONTS[th.font] || FONTS.plex;
  root.style.setProperty("--font-body", f.body);
  root.style.setProperty("--font-head", f.head);
  root.style.setProperty("--font-num", f.num);
  root.style.setProperty("--radius", th.radius);
  root.dataset.theme = th.key;
  root.dataset.mode = th.mode;
  root.dataset.onAcc = th.onAcc || "light";
  root.dataset.ui = th.font === "plex" ? "industrial" : "soft";
  root.style.colorScheme = th.mode;
  return th;
}
